import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { styled, Box, IconButton, Typography, Popover, useTheme } from '@mui/material';
import { ReactComponent as NotificationsIcon } from '../assets/icons/24/notifications.svg';
import { ReactComponent as NotificationsIcon48 } from '../assets/icons/48/notifications.svg';
import SvgIcon from './SvgIcon';
import Button from './Button';
import { TabSwitchContainer, TabSwitchItem, TabSwitchWrapper } from './FilterToolbarStyles';
import { useNotifications } from '../hooks/notifications';
import { getNotificationRoute } from '../constants/notification';
import { PATH } from '../constants/routes';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { getThemeColors } from '../helpers/themeStyles';

const NotificationsButton = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: 48,
  width: 48,
  boxSizing: 'border-box',
  borderRadius: 18,
  backgroundColor: theme.palette.primary.main,
}));

const NotificationRow = styled(Box, {
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})(({ theme, $hovered }) => {
  const colors = getThemeColors(theme);

  return {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: `1px solid ${colors.notificationRowBorder}`,
    cursor: 'pointer',
    padding: 16,
    color: colors.textPrimary,
    '&:last-of-type': {
      borderBottom: 'none',
    },
    ...($hovered && {
      backgroundColor: colors.notificationRowHover,
      borderRadius: 8,
      borderBottom: 'none',
    }),
  };
});

const NotificationsPopover = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = getThemeColors(theme);
  const [filter, setFilter] = useState('all');
  const listRef = useRef(null);
  const {
    data: notifications,
    unreadCount: apiUnreadCount,
    loading,
    loadingMore,
    hasMore,
    markAsRead,
    markAllAsRead,
    refetch,
    loadMore,
  } = useNotifications(filter);

  useInfiniteScroll(listRef, {
    onLoadMore: loadMore,
    hasMore,
    loading,
    loadingMore,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [readOverride, setReadOverride] = useState(new Set());
  const [hoveredId, setHoveredId] = useState(null);
  const isOpen = Boolean(anchorEl);

  const notificationsWithReadStatus = useMemo(() => {
    return notifications.map((n) => ({
      ...n,
      unread: !readOverride.has(n.id) && n.unread,
    }));
  }, [notifications, readOverride]);

  const unreadCount = Math.max(0, (apiUnreadCount ?? 0) - readOverride.size);
  const filteredNotifications =
    filter === 'unread'
      ? notificationsWithReadStatus.filter((n) => n.unread)
      : notificationsWithReadStatus;

  const handleButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
    refetch();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    setReadOverride((prev) => new Set([...prev, ...notifications.map((n) => n.id)]));
  };

  const handleNotificationClick = (notification) => {
    if (notification.unread) {
      markAsRead(notification.id);
      setReadOverride((prev) => new Set([...prev, notification.id]));
    }
    const route = getNotificationRoute(notification);
    if (route) {
      handleClose();
      navigate(route);
    }
  };

  const handleViewAll = () => {
    handleClose();
    navigate(PATH.NOTIFICATIONS);
  };

  return (
    <>
      <Box sx={{ cursor: 'pointer', width: 48, height: 48 }} onClick={handleButtonClick}>
        {isOpen ? (
          <NotificationsButton>
            <SvgIcon icon={NotificationsIcon} size={24} color={colors.activeTextOnAccent} />
          </NotificationsButton>
        ) : (
          <NotificationsIcon48 />
        )}
      </Box>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              width: 480,
              borderRadius: '14px',
              backgroundColor: colors.menuBg,
              overflow: 'hidden',
              border: `1px solid ${colors.borderSubtle}`,
              display: 'flex',
              flexDirection: 'column',
              color: colors.textPrimary,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ padding: '32px 24px 16px' }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 2 }}>Notifications</Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <TabSwitchWrapper>
                <TabSwitchContainer>
                  <TabSwitchItem $active={filter === 'all'} onClick={() => setFilter('all')}>
                    TOUT
                  </TabSwitchItem>
                  <TabSwitchItem $active={filter === 'unread'} onClick={() => setFilter('unread')}>
                    NON LU ({unreadCount})
                  </TabSwitchItem>
                </TabSwitchContainer>
              </TabSwitchWrapper>
              <Typography
                onClick={handleMarkAllAsRead}
                sx={{
                  fontSize: 14,
                  fontWeight: 900,
                  color: 'primary.main',
                  cursor: 'pointer',
                }}
              >
                TOUT MARQUER COMME LU
              </Typography>
            </Box>
          </Box>

          <Box
            ref={listRef}
            sx={{
              minHeight: 0,
              maxHeight: 360,
              overflowY: 'auto',
              padding: '0 8px',
            }}
          >
            {loading ? (
              <Typography
                sx={{ fontSize: 14, color: colors.textSubtle, py: 4, textAlign: 'center' }}
              >
                Chargement...
              </Typography>
            ) : filteredNotifications.length === 0 ? (
              <Typography
                sx={{ fontSize: 14, color: colors.textSubtle, py: 4, textAlign: 'center' }}
              >
                Aucune notification
              </Typography>
            ) : (
              <>
                {filteredNotifications.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    $hovered={hoveredId === notification.id}
                    onMouseEnter={() => setHoveredId(notification.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
                          {notification.title}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 12, fontWeight: 500, color: colors.textSecondary }}
                        >
                          {notification.description}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        <Typography
                          sx={{ fontSize: 14, fontWeight: 500, color: colors.textSubtle }}
                        >
                          {notification.date}
                        </Typography>
                        {notification.unread && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: 'primary.main',
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </NotificationRow>
                ))}
                {loadingMore && (
                  <Typography
                    sx={{ fontSize: 14, color: colors.textSubtle, py: 2, textAlign: 'center' }}
                  >
                    Chargement...
                  </Typography>
                )}
              </>
            )}
          </Box>

          <Box sx={{ flexShrink: 0, margin: '32px 24px 16px' }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleViewAll}
              sx={{ backgroundColor: colors.primaryActionBg, color: colors.primaryActionText }}
            >
              VOIR TOUT
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationsPopover;
